import type { BaseElement } from 'slate';

import type { Length } from '../../../types';
import type {
  AlineContentBlock,
  LocationIdProps,
  SuggestedTableCellEdit,
  SuggestedTableEdit,
  SuggestedTableRowEdit,
} from '../../internal';
import type { Shade } from '../GlobalStyles';

export type InsertTableRowLocation = 'above' | 'below';

export type InsertTableColumnLocation = 'left' | 'right';

export type TableCellType = 'table-cell';
export const tableCellType: TableCellType = 'table-cell';

export type TableRowType = 'table-row';
export const tableRowType: TableRowType = 'table-row';

export type TableType = 'table';
export const tableType: TableType = 'table';

export type TableCellProps = Omit<TableCellElement, 'children'>;
export type TableRowProps = Omit<TableRowElement, 'children'>;
export type TableProps = Omit<TableElement, 'children'>;

export interface TableCellElement extends BaseElement, LocationIdProps {
  children: AlineContentBlock[];
  type: TableCellType;
  borders?: PartialTableBorders;
  columnSpan?: number;
  margins?: Partial<TableCellMargins>;
  shade?: Shade;
  suggestedEdits?: SuggestedTableCellEdit[] | null;
  /**
   * Property controlling whether this cell is part of a vertically merged cell.
   *
   * A value of 'restart' means that this cell is not part of any previous vertically merged cell,
   * but if the subsequent cell in the column is 'continue', that subsequent cell extends this cell.
   *
   * A value of 'continue' means that if the previous cell in the column is 'restart' or 'continue',
   * this cell extends that previous cell. If the previous cell has a verticalMerge property of
   * undefined, then this 'continue' cell is treated as if it were 'restart', thus forming the start
   * of a new vertically merged cell that excludes the undefined cell.
   *
   * Note that a value of undefined is distinct from either 'restart' or 'continue' (i.e. neither of
   * these is the default value). A cell with a verticalMerge property of undefined is never part of
   * a vertically merged cell.
   */
  verticalMerge?: 'restart' | 'continue';
  /**
   * The preferred width of the table cell. Legacy lengths are measured in inches.
   * @default auto
   */
  width?: TableWidth | Length.Legacy;
}

export interface TableRowElement extends BaseElement, LocationIdProps {
  cantSplit: boolean;
  children: TableCellElement[];
  type: TableRowType;
  /**
   * The exact height of the table row, beyond which content is clipped. Legacy lengths are measured
   * in inches. If omitted, the height of the row depends on its content.
   */
  height?: Length.Twip | Length.Legacy;
  /**
   * The minimum height of the row. Legacy lengths are measured in inches.
   */
  minHeight?: Length.Twip | Length.Legacy;
  /**
   * The shade of the table row. Contrary to what the specification says, this property appears to
   * be unused for rendering tables in Word, so we do not render it in Aline either.
   */
  shade?: Shade;
  suggestedEdits?: SuggestedTableRowEdit[] | null;
}

export interface TableElement extends BaseElement, LocationIdProps {
  children: TableRowElement[];
  type: TableType;
  align?: TableAlign;
  borders?: PartialTableBorders;
  /**
   * The preferred widths of each grid column.
   */
  gridColumns?: Length.Twip[];
  /**
   * The indent of the table.
   *
   * We favor align over indent. If alignment is anything other than center or right, ignore the
   * indent. In Word, indent and align cannot coexist so this should never happen though.
   *
   * Percentage and auto lengths are valid, but are ignored for this property.
   */
  indent?: TableWidth | Length.LegacyInch;
  /**
   * The table layout algorithm to use.
   * @default autofit
   */
  layout?: TableLayout;
  margins?: Partial<TableCellMargins>;
  /**
   * The shade of the table. Contrary to what the specification says, this property appears to be
   * unused for rendering tables in Word, so we do not render it in Aline either.
   */
  shade?: Shade;
  suggestedEdits?: SuggestedTableEdit[] | null;
  /**
   * The preferred width of the table. Legacy lengths are measured in inches.
   * @default auto
   */
  width?: TableWidth | Length.Legacy;
}

/**
 * Table alignment
 */
export type TableAlign = 'center' | 'left' | 'right';

/**
 * Table layout algorithm. AutoFit uses the content of each cell to determine the final column
 * widths, whereas fixed uses only the preferred widths.
 */
export type TableLayout = 'autofit' | 'fixed';

/**
 * A length type used for various table properties, including cell spacing, preferred widths and
 * table margins.
 *
 * A length of auto means that the width is determined by the table layout algorithm.
 *
 * Nil means that the width is zero.
 *
 * A percentage for the preferred width of the table is relative to the extents of the parent
 * element (usually the distance between the page margins). Otherwise, the percentage is relative to
 * the width of the table (TODO: determine how this behaves when the table's width depends on the
 * widths of its cells).
 * https://linear.app/aline/issue/EDI-266/determine-how-percentage-table-cell-widths-should-be-handled-when-the
 */
export type TableWidth = Length.Auto | Length.Twip | Length.Nil | Length.Pct;

/**
 * Table cell margins. Legacy lengths are measured in inches.
 */
export interface TableCellMargins<Unit = TableWidth | Length.Legacy> {
  bottom: Unit;
  left: Unit;
  right: Unit;
  top: Unit;
}

export type TableBorderPosition =
  | 'bottom'
  | 'insideH' // Interior horizontal border.
  | 'insideV' // Interior vertical border.
  | 'left'
  | 'right'
  | 'top';

export type TableBorders = Record<TableBorderPosition, TableBorder>;
export type PartialTableBorders = Partial<Record<TableBorderPosition, Partial<TableBorder>>>;

export type TableBorderWidths = Record<TableBorderPosition, number>;

export interface TableBorder {
  color: string;
  size: TableBorderSize;
  style: TableBorderStyle;
  /**
   * Distance from text to the border's inner edge, in points (OOXML `w:space`).
   * Missing means 0. Used for paragraph border layout; tables typically emit 0.
   */
  space?: number;
}

// Border weight *in pt*.
export const tableBorderSizes = [0, 0.5, 0.75, 1, 1.5, 2.25, 3, 4.5, 6] as const;

// Border weight *in pt*.
export type TableBorderSize = (typeof tableBorderSizes)[number];

export type TableBorderStyle =
  | 'dashed'
  | 'dotted'
  | 'double'
  | 'groove'
  | 'hidden'
  | 'inset'
  | 'none'
  | 'outset'
  | 'ridge'
  | 'solid';

/**
 * Border style weights, used to determine which border is drawn in the event of a conflict.
 *
 * Note that this differs slightly from the table of border numbers given in the specification,
 * since these numbers have already been multiplied by the number of lines comprising each style.
 * For example, although the 'double' style has a border number of 3, this has been multiplied by
 * its number of lines (2), resulting in an actual weight of 6.
 *
 * The order of keys in this object is important, since if two different styles have equal weight
 * (such as 'triple' and 'outset'), the style appearing earlier in this object is used.
 */
/* eslint-disable sort-keys */
export const tableBorderStyleWeights: Record<TableBorderStyle, number> = {
  none: 0,
  hidden: 0,
  solid: 1,
  double: 3 * 2,
  dotted: 4,
  dashed: 5,
  ridge: 22,
  groove: 23,
  outset: 24,
  inset: 25,
};
